/**
 * «Сайты, где работает окно» (экран 7). Клиент вписывает домен — бэкенд сверяет
 * точный origin (схема://хост[:порт]), поэтому храним и без www, и с www.
 *
 * В плашках сайт показываем по-человечески: «кофесервис.рф», а не «xn--…»;
 * схему и порт — только если они не обычные (http, :8080).
 */

/** Punycode → Юникод (RFC 3492, только декодирование) — для показа кириллических доменов. */
function decodePunycode(input: string): string {
  const base = 36,
    tMin = 1,
    tMax = 26,
    skew = 38,
    damp = 700;
  const out: number[] = [];
  let i = 0,
    n = 128,
    bias = 72;
  const basic = input.lastIndexOf('-');
  for (let j = 0; j < Math.max(basic, 0); j++) out.push(input.charCodeAt(j));
  const digit = (c: number) =>
    c - 48 < 10 ? c - 22 : c - 65 < 26 ? c - 65 : c - 97 < 26 ? c - 97 : base;
  const adapt = (delta: number, num: number, first: boolean) => {
    let k = 0;
    delta = first ? Math.floor(delta / damp) : delta >> 1;
    delta += Math.floor(delta / num);
    for (; delta > ((base - tMin) * tMax) >> 1; k += base)
      delta = Math.floor(delta / (base - tMin));
    return Math.floor(k + ((base - tMin + 1) * delta) / (delta + skew));
  };
  for (let idx = basic > 0 ? basic + 1 : 0; idx < input.length; ) {
    const oldi = i;
    for (let w = 1, k = base; ; k += base) {
      if (idx >= input.length) throw new Error('punycode');
      const d = digit(input.charCodeAt(idx++));
      if (d >= base) throw new Error('punycode');
      i += d * w;
      const t = k <= bias ? tMin : k >= bias + tMax ? tMax : k - bias;
      if (d < t) break;
      w *= base - t;
    }
    const len = out.length + 1;
    bias = adapt(i - oldi, len, oldi === 0);
    n += Math.floor(i / len);
    i %= len;
    out.splice(i++, 0, n);
  }
  return String.fromCodePoint(...out);
}

function toUnicodeHost(host: string): string {
  return host
    .split('.')
    .map((p) => {
      if (!p.startsWith('xn--')) return p;
      try {
        return decodePunycode(p.slice(4));
      } catch {
        return p;
      }
    })
    .join('.');
}

interface Site {
  scheme: 'http' | 'https';
  host: string; // в Юникоде, без www
  port: string; // '' — порт по умолчанию
}

function parse(input: string): Site | null {
  let s = input.trim();
  if (!s) return null;
  const m = /^([a-z]+):\/\//i.exec(s);
  const scheme = (m?.[1] ?? 'https').toLowerCase();
  if (scheme !== 'http' && scheme !== 'https') return null;
  if (m) s = s.slice(m[0].length);
  const hostPort = (s.split(/[/?#]/)[0] ?? '').toLowerCase();
  const pm = /:(\d{1,5})$/.exec(hostPort);
  let host = pm ? hostPort.slice(0, -pm[0].length) : hostPort;
  host = toUnicodeHost(host).replace(/^www\./, '');
  const port =
    pm && !(scheme === 'https' && pm[1] === '443') && !(scheme === 'http' && pm[1] === '80')
      ? pm[1]
      : '';
  return { scheme, host, port } as Site;
}

const show = (x: Site) =>
  x.scheme === 'https' && !x.port ? x.host : `${x.scheme}://${x.host}${x.port ? `:${x.port}` : ''}`;

/** «https://www.kofeservice.ru/page» → «kofeservice.ru»; «http://shop.ru:8080» — как есть. */
export function siteHost(input: string): string {
  const x = parse(input);
  return x ? show(x) : '';
}

const HOST_RE = /^(?=.{3,253}$)([a-z0-9а-яё]([a-z0-9а-яё-]{0,61}[a-z0-9а-яё])?\.)+[a-zа-яё]{2,}$/i;

/** Адрес сайта → origins для бэкенда, или null, если не похоже на сайт. */
export function originsFor(input: string): string[] | null {
  const x = parse(input);
  if (!x || !HOST_RE.test(x.host)) return null;
  // Кириллический домен браузер присылает в punycode (xn--…) — храним так же.
  let ascii: string;
  try {
    ascii = new URL(`https://${x.host}`).hostname;
  } catch {
    return null;
  }
  const port = x.port ? `:${x.port}` : '';
  return [`${x.scheme}://${ascii}${port}`, `${x.scheme}://www.${ascii}${port}`];
}

/** origins с бэкенда → уникальные сайты для плашек. */
export function hostsOf(origins: string[]): string[] {
  const out: string[] = [];
  for (const o of origins) {
    const h = siteHost(o);
    if (h && !out.includes(h)) out.push(h);
  }
  return out;
}
